<!--
  ConnectionStatus — tells the operator the realtime feed is not live.

  Mirrors the design-system component's decisions in the widget's own palette:

  - `online` renders NOTHING. A permanent "connected" badge is noise that
    trains people to ignore the strip, and the only state worth interrupting
    someone for is the one where their queue has quietly stopped being true.
  - Severity rides on FILL and GLYPH SHAPE, never hue alone — an arc for
    "working on it", a triangle for "stopped" — so it survives greyscale and
    every CVD type.
  - The spinner is an ARC rather than a ring, so the shape still reads as
    in-progress when the rotation is suppressed by prefers-reduced-motion
    (which App.svelte enforces globally).
  - aria-live is `polite` while reconnecting (the client is handling it) and
    `assertive` once offline (the operator has to).

  There is deliberately NO manual Retry control. `RealtimeClient` already has a
  reconnect timer pending, and calling `connect()` alongside it risks opening a
  second socket. A button that might double-connect is worse than no button.
-->
<script lang="ts">
  import { connection } from '../lib/realtime/connection.svelte.js';

  const state = $derived(connection.state);
  const label = $derived(
    state === 'offline'
      ? 'Not live — new messages may be missing'
      : 'Reconnecting…',
  );
</script>

{#if state !== 'online'}
  <div
    class="strip"
    class:offline={state === 'offline'}
    role="status"
    aria-live={state === 'offline' ? 'assertive' : 'polite'}
    aria-atomic="true"
  >
    {#if state === 'offline'}
      <svg class="glyph" viewBox="0 0 16 16" aria-hidden="true">
        <path
          fill="currentColor"
          d="M8 1.5 15 14H1L8 1.5Zm0 4.25a.75.75 0 0 0-.75.75v2.75a.75.75 0 0 0 1.5 0V6.5A.75.75 0 0 0 8 5.75Zm0 5.5a.9.9 0 1 0 0 1.8.9.9 0 0 0 0-1.8Z"
        />
      </svg>
    {:else}
      <svg class="glyph spin" viewBox="0 0 16 16" aria-hidden="true">
        <path
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          d="M14 8a6 6 0 1 1-1.76-4.24"
        />
      </svg>
    {/if}
    <span>{label}</span>
  </div>
{/if}

<style>
  .strip {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 6px 14px;
    font-size: 12px;
    font-weight: 600;
    line-height: 1.3;
    /* Reconnecting is usually seconds long, so it stays a tint rather than a
       solid bar across the top of the operator's work. */
    background: var(--widget-accent-tint);
    color: var(--widget-accent-deep);
    border-bottom: 1px solid color-mix(in oklch, var(--widget-accent-deep) 25%, transparent);
  }
  /* Offline goes solid — the luminance jump is the signal, matching the
     outline-vs-fill rule the SLA chip uses. */
  .strip.offline {
    background: var(--widget-danger);
    color: var(--widget-bg-elevated);
    border-bottom-color: var(--widget-danger);
  }

  .glyph {
    width: 14px;
    height: 14px;
    flex: 0 0 auto;
  }
  .spin {
    animation: spin 1s linear infinite;
    transform-origin: 50% 50%;
  }
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
</style>
